import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import api from "../../lib/api";
import { Loading, inr } from "../../components/shared";
import { useSocietyFeatures } from "../../context/SocietyFeatureContext";
import {
  Home,
  Users,
  IndianRupee,
  MessageSquareWarning,
  Wrench,
  TrendingUp,
  FileText,
  Receipt,
  Megaphone,
  UserPlus,
  BarChart3,
  Zap,
  Clock,
  ArrowRight,
  CircleDot,
} from "lucide-react";

const SUMMARY_TONES = {
  blue: "bg-[#2563EB]/10 text-[#2563EB]",
  green: "bg-emerald-100 text-emerald-600",
  pink: "bg-pink-100 text-pink-600",
  orange: "bg-orange-100 text-orange-600",
  purple: "bg-violet-100 text-violet-600",
  teal: "bg-teal-100 text-teal-600",
};

function SummaryCard({ icon: Icon, label, value, tone, testid }) {
  return (
    <div
      className="rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_6px_24px_rgba(15,43,99,0.05)] transition-transform duration-200 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(15,43,99,0.08)]"
      data-testid={testid}
    >
      <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${SUMMARY_TONES[tone]}`}>
        <Icon className="h-6 w-6" />
      </div>
      <div className="mt-4 font-display text-3xl font-bold tracking-tight text-[#0F2B63]">{value}</div>
      <div className="mt-0.5 text-sm text-slate-500">{label}</div>
    </div>
  );
}

const QUICK = [
  { label: "Generate Monthly Bill", icon: FileText, to: "/app/finance-preview?section=billing", tone: "bg-emerald-50 text-emerald-600" },
  { label: "Record Payment", icon: Receipt, to: "/app/finance-preview?section=receipts", tone: "bg-rose-50 text-rose-500" },
  { label: "Send Notice", icon: Megaphone, to: "/app/notices", tone: "bg-sky-50 text-sky-600" },
  { label: "Register Complaint", icon: MessageSquareWarning, to: "/app/complaints", tone: "bg-amber-50 text-amber-600" },
  { label: "Add New Member", icon: UserPlus, to: "/app/directory", tone: "bg-violet-50 text-violet-600" },
  { label: "View Reports", icon: BarChart3, to: "/app/finance-preview?section=reports", tone: "bg-indigo-50 text-indigo-600" },
];

function Donut({ data, centerValue, centerLabel, testid }) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  return (
    <div className="flex items-center gap-6" data-testid={testid}>
      <div className="relative h-36 w-36 shrink-0">
        <ResponsiveContainer width="100%" height="100%" minHeight={144}>
          <PieChart>
            <Pie
              data={total ? data : [{ name: "none", value: 1, color: "#E2E8F0" }]}
              dataKey="value"
              innerRadius={48}
              outerRadius={68}
              paddingAngle={total ? 2 : 0}
              stroke="none"
              isAnimationActive={false}
            >
              {(total ? data : [{ color: "#E2E8F0" }]).map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-display text-2xl font-bold text-[#0F2B63]">{centerValue}</span>
          <span className="text-[11px] text-slate-400">{centerLabel}</span>
        </div>
      </div>
      <div className="flex-1 space-y-2.5">
        {data.map((item) => (
          <div key={item.name} className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 text-slate-600">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: item.color }} />
              {item.name}
            </span>
            <span className="font-semibold text-[#0F2B63]">
              {item.value}
              {total > 0 && (
                <span className="ml-1 text-xs font-normal text-slate-400">
                  ({Math.round((item.value / total) * 100)}%)
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const ACTIVITY_STYLES = {
  payment: { icon: Receipt, color: "bg-emerald-100 text-emerald-600" },
  bill: { icon: FileText, color: "bg-[#2563EB]/10 text-[#2563EB]" },
  invoice: { icon: FileText, color: "bg-[#2563EB]/10 text-[#2563EB]" },
  notice: { icon: Megaphone, color: "bg-rose-100 text-rose-500" },
  member: { icon: Users, color: "bg-violet-100 text-violet-600" },
  complaint: { icon: MessageSquareWarning, color: "bg-amber-100 text-amber-600" },
  ticket: { icon: MessageSquareWarning, color: "bg-amber-100 text-amber-600" },
};

function activityStyle(entity = "") {
  const key = Object.keys(ACTIVITY_STYLES).find((k) => entity.toLowerCase().includes(k));
  return ACTIVITY_STYLES[key] || { icon: CircleDot, color: "bg-slate-100 text-slate-500" };
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { society } = useOutletContext() || {};
  const [data, setData] = useState(null);
  const [perms, setPerms] = useState({});
  const [setupDash, setSetupDash] = useState(null);
  const [financeDash, setFinanceDash] = useState(null);
  const [helpdesk, setHelpdesk] = useState(null);
  const { byKey, features } = useSocietyFeatures();

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => setData({}));
    api.get("/society-setup/context").then((r) => setPerms(r.data.permissions || {})).catch(() => setPerms({}));
  }, []);

  useEffect(() => {
    const can = (m, a) => Boolean(perms[m]?.includes(a));
    const featureOn = (k) => Boolean(byKey?.(k)?.allowed);
    if (can("profile", "view")) {
      api.get("/society-setup/dashboard").then((r) => setSetupDash(r.data)).catch(() => {});
    }
    if (featureOn("sm03_billing") && can("finance_reports", "view")) {
      api.get("/society-finance/dashboard").then((r) => setFinanceDash(r.data)).catch(() => {});
    }
    if (featureOn("sm05_helpdesk") && can("helpdesk_reports", "view")) {
      api.get("/society-helpdesk/dashboard").then((r) => setHelpdesk(r.data)).catch(() => {});
    }
  }, [perms, byKey, features]);

  if (!data) return <Loading />;

  const units = setupDash?.counts?.units ?? data.units ?? 0;
  const members = data.members ?? 0;
  const billed = financeDash?.billed ?? 0;
  const collected = financeDash?.collected ?? 0;
  const collectionPct = financeDash ? (billed > 0 ? Math.round((collected / billed) * 100) : 0) : null;

  const billingData = financeDash
    ? [
        { name: "Collected", value: Math.round(collected), color: "#10B981" },
        { name: "Outstanding", value: Math.round(financeDash.outstanding ?? 0), color: "#F43F5E" },
      ]
    : null;

  const complaintData = helpdesk
    ? [
        { name: "Open", value: helpdesk.new ?? 0, color: "#2563EB" },
        { name: "In Progress", value: helpdesk.in_progress ?? 0, color: "#F59E0B" },
        { name: "Resolved", value: helpdesk.resolved ?? 0, color: "#10B981" },
      ]
    : null;

  const activities = data.recent_activities || [];

  return (
    <div className="space-y-6" data-testid="society-dashboard">
      {/* Welcome banner */}
      <div className="relative overflow-hidden rounded-3xl border border-[#2563EB]/10 bg-gradient-to-r from-[#E8F1FE] via-[#EEF4FF] to-[#F2F8FF] p-6 md:p-8">
        <div className="relative z-10 max-w-lg">
          <div className="text-sm font-medium text-slate-500">Welcome to</div>
          <h1 className="mt-1 font-display text-2xl font-bold leading-tight text-[#0F2B63] md:text-3xl" data-testid="dashboard-welcome-society">
            {society?.name || "Your Society"}
          </h1>
          <p className="mt-2 text-sm text-slate-600 md:text-base">
            Manage members, billing, communication, maintenance and more — all in one place.
          </p>
        </div>
        <img
          src="/society-building.jpg"
          alt=""
          className="pointer-events-none absolute right-0 top-1/2 hidden h-[115%] -translate-y-1/2 object-contain opacity-95 mix-blend-multiply md:block"
        />
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <SummaryCard icon={Home} label="Total Units" value={units ?? "—"} tone="blue" testid="stat-units" />
        <SummaryCard icon={Users} label="Total Members" value={members} tone="green" testid="stat-members" />
        <SummaryCard icon={TrendingUp} label="Monthly Collection %" value={collectionPct === null ? "—" : `${collectionPct}%`} tone="pink" testid="stat-collection" />
        <SummaryCard icon={IndianRupee} label="Total Outstanding" value={inr(data.outstanding)} tone="orange" testid="stat-outstanding" />
        <SummaryCard icon={MessageSquareWarning} label="Pending Complaints" value={data.open_complaints ?? 0} tone="purple" testid="stat-complaints" />
        <SummaryCard icon={Wrench} label="Open Service Requests" value={data.open_requirements ?? 0} tone="teal" testid="stat-services" />
      </div>

      {/* Quick actions */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_6px_24px_rgba(15,43,99,0.05)]">
        <div className="mb-4 flex items-center gap-2">
          <Zap className="h-5 w-5 text-[#F97316]" />
          <h2 className="font-display text-lg font-bold text-[#0F2B63]">Quick Actions</h2>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {QUICK.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.label}
                onClick={() => navigate(action.to)}
                data-testid={`quick-${action.label.replace(/\s/g, "-").toLowerCase()}`}
                className="flex flex-col items-center gap-2 rounded-2xl border border-slate-100 bg-white p-4 text-center transition-all duration-200 hover:-translate-y-1 hover:border-[#2563EB]/20 hover:shadow-md"
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${action.tone}`}>
                  <Icon className="h-6 w-6" />
                </span>
                <span className="text-xs font-medium leading-tight text-slate-600">{action.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Recent activities */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_6px_24px_rgba(15,43,99,0.05)]">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-[#2563EB]" />
            <h3 className="font-display text-lg font-bold text-[#0F2B63]">Recent Activities</h3>
          </div>
        </div>
        {activities.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center" data-testid="activities-empty">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
              <Clock className="h-6 w-6 text-slate-300" />
            </div>
            <p className="mt-3 text-sm text-slate-400">No recent activity yet. Actions across your society will appear here.</p>
          </div>
        ) : (
          <div className="space-y-1">
            {activities.map((activity) => {
              const style = activityStyle(activity.entity);
              const Icon = style.icon;
              return (
                <div key={activity.log_id} className="flex items-center gap-3 border-b border-slate-50 py-3 last:border-0">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${style.color}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium capitalize text-slate-700">
                    {activity.action} {activity.entity}
                  </span>
                  <span className="shrink-0 text-xs text-slate-400">
                    {(activity.timestamp || "").slice(0, 16).replace("T", " ")}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Visual analytics */}
      {(billingData || complaintData) && (
        <div className="grid gap-6 lg:grid-cols-2">
          {billingData && (
            <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_6px_24px_rgba(15,43,99,0.05)]">
              <div className="mb-4 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-emerald-600" />
                <h3 className="font-display text-lg font-bold text-[#0F2B63]">Billing Status</h3>
              </div>
              <Donut
                data={billingData}
                centerValue={collectionPct === null ? "—" : `${collectionPct}%`}
                centerLabel="Collected"
                testid="billing-donut"
              />
            </div>
          )}
          {complaintData && (
            <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_6px_24px_rgba(15,43,99,0.05)]">
              <div className="mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5 text-[#2563EB]" />
                <h3 className="font-display text-lg font-bold text-[#0F2B63]">Complaints Status</h3>
              </div>
              <Donut
                data={complaintData}
                centerValue={helpdesk.open ?? 0}
                centerLabel="Open"
                testid="complaints-donut"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
